import type {
  StripePaymentMethodDomainMethodStatus,
  StripePaymentMethodDomainStatus,
} from '@/lib/services/domains/domains.types'

export interface StripePaymentMethodDomainStatusMeta {
  label: string
  variant: 'success' | 'warning' | 'destructive' | 'muted'
  description: string
  actionLabel: string | null
}

export function getStripePaymentMethodDomainStatusMeta(
  stripePaymentMethodDomain?: StripePaymentMethodDomainStatus
): StripePaymentMethodDomainStatusMeta {
  const status = stripePaymentMethodDomain?.status

  switch (status) {
    case 'registered':
      return {
        label: 'Stripe registered',
        variant: 'success',
        description: 'Wallet registration is active in Stripe for this domain.',
        actionLabel: null,
      }
    case 'disabled':
      return {
        label: 'Stripe disabled',
        variant: 'warning',
        description: 'Stripe has this domain but wallet registration is disabled.',
        actionLabel: 'Re-enable in Stripe',
      }
    case 'not_registered':
      return {
        label: 'Stripe not registered',
        variant: 'destructive',
        description: 'Register this domain in Stripe to make storefront wallets eligible.',
        actionLabel: 'Register with Stripe',
      }
    case 'error':
      return {
        label: 'Stripe status unavailable',
        variant: 'warning',
        description: stripePaymentMethodDomain?.error || 'Stripe status could not be loaded.',
        actionLabel: null,
      }
    case 'not_eligible':
      return {
        label: 'Stripe unavailable',
        variant: 'muted',
        description: getStripePaymentMethodDomainIneligibleMessage(stripePaymentMethodDomain),
        actionLabel: null,
      }
    case undefined:
    default:
      return {
        label: 'Stripe status unavailable',
        variant: 'muted',
        description: 'Stripe registration status has not been loaded yet.',
        actionLabel: null,
      }
  }
}

export function getStripePaymentMethodDomainIneligibleMessage(
  stripePaymentMethodDomain?: StripePaymentMethodDomainStatus
): string {
  switch (stripePaymentMethodDomain?.reason) {
    case 'domain_not_verified':
      return 'Verify this domain in OneStem before registering it with Stripe.'
    case 'missing_connect_account':
      return 'Connect Stripe for this florist before registering domains.'
    case null:
    case undefined:
    default:
      return 'This domain is not currently eligible for Stripe wallet registration.'
  }
}

export function getStripePaymentMethodDomainMethodStatuses(
  stripePaymentMethodDomain?: Partial<
    Pick<
      StripePaymentMethodDomainStatus,
      'payment_method_statuses' | 'apple_pay_status' | 'google_pay_status'
    >
  >
): StripePaymentMethodDomainMethodStatus[] {
  const paymentMethodStatuses = stripePaymentMethodDomain?.payment_method_statuses
  // Keep the admin domain view focused on the two storefront launch wallets for v1.
  const allowedPaymentMethods = new Set(['apple_pay', 'google_pay'])

  if (Array.isArray(paymentMethodStatuses) && paymentMethodStatuses.length) {
    return paymentMethodStatuses.filter((status) => allowedPaymentMethods.has(status.key))
  }

  return [
    {
      key: 'apple_pay',
      label: 'Apple Pay',
      status: stripePaymentMethodDomain?.apple_pay_status,
    },
    {
      key: 'google_pay',
      label: 'Google Pay',
      status: stripePaymentMethodDomain?.google_pay_status,
    },
  ].flatMap(({ key, label, status }) => (status ? [{ key, label, status }] : []))
}
